import React, { useEffect, useState } from "react";
import { getLivisToken } from "../../helpers/storage";
import { useLocation } from "react-router-dom";
import "./CookieConsentBanner.scss";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "livis_cookie_consent"; // values: accepted | rejected

const CookieConsentBanner: React.FC = () => {
  const [decision, setDecision] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  });
  const {t} = useTranslation();

  // Track token in a variable and react to its changes
  const location = useLocation();
  const [token, setToken] = useState<string | null>(() => getLivisToken());

  // Update token on route changes (covers navigation after login)
  useEffect(() => {
    setToken(getLivisToken());
  }, [location.pathname]);

  // Post-mount microtask update to catch immediate writes
  useEffect(() => {
    const t = setTimeout(() => setToken(getLivisToken()), 0);
    return () => clearTimeout(t);
  }, []);

  // React to token changes explicitly if needed
  useEffect(() => {
    // If token appears and there is no stored consent, component will render the banner
    // This effect ensures we re-check decision in case external code modifies localStorage
    if (token) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored !== decision) setDecision(stored);
      } catch (e) {
        // ignore storage errors
      }
    }
  }, [token]);

  // No need for an effect to read decision; we read synchronously above to avoid flash

  const handleChoose = (value: "accepted" | "rejected") => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      // ignore storage errors
    }
    setDecision(value);
  };

  const loggedIn = !!token;
  if (!loggedIn) return null; // only after login
  if (decision === "accepted" || decision === "rejected") return null; // already decided

  return (
    <div
      className="livis-cookie-consent-banner-container"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie Consent"
    >
      <h4 className="livis-cookie-consent-banner-title">{t("This platform uses cookies to enhance your experience.")}</h4>
      <p className="livis-cookie-consent-banner-text">
{t("We use cookies and similar technologies to understand how you interact with our application, personalize your experience, and improve the performance of our AI visual inspection services. We may analyze usage insights to optimize features, support product improvements, and deliver a secure and reliable service.")}
{/* <a href="" className="livis-cookie-consent-banner-text-anchor">{t("To learn more about how we use cookies and process data, please review our Privacy Policy.")}</a> */}
      </p>
      <div className="livis-cookie-consent-banner-actions">
        <button
          className="livis-cookie-consent-banner-button livis-cookie-consent-banner-button-reject"
          onClick={() => handleChoose("rejected")}
        >
          {t("Reject all")}
        </button>
        <button
          className="livis-cookie-consent-banner-button livis-cookie-consent-banner-button-accept"
          onClick={() => handleChoose("accepted")}
        >
          {t("Accept all")}
        </button>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
